import React, { useEffect, useState } from 'react';
import SEO from '../components/SEO';
import PricingToggle from '../components/careerplans/PricingToggle';
import MembershipCard from '../components/careerplans/MembershipCard';
import BenefitsComparison from '../components/careerplans/BenefitsComparison';
import FAQSection from '../components/careerplans/FAQSection';
import { API_ENDPOINTS } from '../config/constants';
import '../styles/career-plans.css';

function normalizeSectionTitle(title) {
  return String(title || '').replace(/^\s{0,3}#{1,6}\s*/, '').trim();
}

function getBenefitIcon(title) {
  const normalized = String(title || '').toLowerCase();
  if (normalized.includes('learn')) return 'L';
  if (normalized.includes('practice')) return 'P';
  if (normalized.includes('share')) return 'S';
  if (normalized.includes('mentor')) return 'M';
  if (normalized.includes('network')) return 'N';
  if (normalized.includes('lead')) return 'D';
  return String(title || 'B').charAt(0).toUpperCase();
}

function CareerPlans() {
  const [careerMembershipsConfig, setCareerMembershipsConfig] = useState(null);
  const [activePeriod, setActivePeriod] = useState('monthly');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    const fetchCareerPlans = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const response = await fetch(API_ENDPOINTS.CAREER_PLANS);
        if (!response.ok) {
          throw new Error('Unable to load memberships data');
        }

        const payload = await response.json();
        const config = payload?.data || payload;

        if (!config || !Array.isArray(config.pricingPeriods)) {
          throw new Error('Invalid memberships payload');
        }

        setCareerMembershipsConfig(config);
        setActivePeriod(config.pricingPeriods[0]?.id || 'monthly');
      } catch (error) {
        console.error('Career plans fetch error:', error);
        setLoadError('We could not load membership details right now. Please try again shortly.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCareerPlans();
  }, []);

  if (isLoading) {
    return (
      <main className="nh-home career-plans-page">
        <SEO pageName="careerPlans" />
        <section className="band bg-light">
          <div className="wrap">
            <h1 className="section-title">Loading memberships...</h1>
            <p className="section-sub">Please wait while we load the latest career plans.</p>
          </div>
        </section>
      </main>
    );
  }

  if (loadError || !careerMembershipsConfig) {
    return (
      <main className="nh-home career-plans-page">
        <SEO pageName="careerPlans" />
        <section className="band bg-light">
          <div className="wrap">
            <h1 className="section-title">Career Memberships</h1>
            <p className="section-sub">{loadError || 'Something went wrong while loading memberships.'}</p>
            <a className="btn btn-red btn-sm" href="/contact">Contact Us</a>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="nh-home career-plans-page">
      <SEO pageName={careerMembershipsConfig.seo.pageName} />

      <section className="career-hero bg-navy band">
        <div className="wrap career-hero__content">
          <p className="career-hero__eyebrow">Career Memberships</p>
          <h1 className="section-title">{careerMembershipsConfig.hero.title}</h1>
          <p className="section-sub">{careerMembershipsConfig.hero.subtitle}</p>
          <a className="btn btn-red btn-pill" href={`#${careerMembershipsConfig.hero.primaryCtaTargetId}`}>
            {careerMembershipsConfig.hero.primaryCtaLabel}
          </a>
        </div>
      </section>

      <section className="career-why-member band bg-light" aria-labelledby="career-why-member-title">
        <div className="wrap">
          <div className="career-why-member__head head-block">
            <p className="eyebrow">Membership Value</p>
            <h2 id="career-why-member-title" className="section-title">{normalizeSectionTitle(careerMembershipsConfig.whyMembership.title)}</h2>
            <p className="section-sub">{careerMembershipsConfig.whyMembership.description}</p>
          </div>
          <ul className="career-why-member__grid">
            {careerMembershipsConfig.whyMembership.points.map((point) => (
              <li key={point}>
                <span className="career-why-member__tick" aria-hidden="true">✓</span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="membership-plans" className="career-plans band" aria-labelledby="membership-plans-title">
        <div className="wrap">
        <h2 id="membership-plans-title" className="section-title">Choose your membership by growth stage</h2>
        <PricingToggle
          periods={careerMembershipsConfig.pricingPeriods}
          activePeriod={activePeriod}
          onPeriodChange={setActivePeriod}
        />
        <div className="career-plans__grid">
          {careerMembershipsConfig.memberships.map((membership) => (
            <MembershipCard
              key={membership.id}
              membership={membership}
              activePeriod={activePeriod}
            />
          ))}
        </div>
        </div>
      </section>

      <section className="career-grouped-benefits band bg-light" aria-labelledby="career-grouped-benefits-title">
        <div className="wrap">
          <div className="career-grouped-benefits__head head-block">
            <p className="eyebrow">Outcome Tracks</p>
            <h2 id="career-grouped-benefits-title" className="section-title">{normalizeSectionTitle('Benefits grouped by outcomes')}</h2>
            <p className="section-sub">Pick the kind of momentum you need now: learn faster, practice deeper, grow with guidance, and lead with clarity.</p>
          </div>
          <div className="career-grouped-benefits__grid">
            {careerMembershipsConfig.groupedBenefits.map((group) => (
              <article key={group.id} className="career-grouped-benefits__card">
                <h3>{group.title}</h3>
                <ul>
                  {group.points.map((point) => (
                    <li key={point}>
                      <span className="career-grouped-benefits__tick" aria-hidden="true">✓</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="band">
        <BenefitsComparison
          title={careerMembershipsConfig.comparison.title}
          rows={careerMembershipsConfig.comparison.rows}
          memberships={careerMembershipsConfig.memberships}
        />
      </div>

      <section className="career-journey band bg-light" aria-labelledby="career-journey-title">
        <div className="wrap">
          <div className="career-journey__head head-block">
            <p className="eyebrow">Progression Path</p>
            <h2 id="career-journey-title" className="section-title">{normalizeSectionTitle(careerMembershipsConfig.journey.title)}</h2>
            <p className="section-sub">Move stage by stage with a clear transformation at each level, from exploration to leadership impact.</p>
          </div>
          <div className="career-journey__timeline">
            {careerMembershipsConfig.journey.steps.map((step, index) => {
              const stepNumber = index + 1;
              const isLastStep = index === careerMembershipsConfig.journey.steps.length - 1;

              return (
                <article key={step.id} className="career-journey__step">
                  <div className="career-journey__step-top">
                    <span className="career-journey__step-num">0{stepNumber}</span>
                    {!isLastStep ? <span className="career-journey__step-arrow" aria-hidden="true">→</span> : null}
                  </div>
                  <h3>{step.label}</h3>
                  <p>{step.transformation}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="career-membership-benefits band" aria-labelledby="career-membership-benefits-title">
        <div className="wrap">
          <div className="career-membership-benefits__head head-block">
            <p className="eyebrow">Member Experience</p>
            <h2 id="career-membership-benefits-title" className="section-title">{normalizeSectionTitle(careerMembershipsConfig.membershipBenefits.title)}</h2>
            <p className="section-sub">Every membership is designed to help you learn, execute, and grow through practical support and community.</p>
          </div>
          <div className="career-membership-benefits__grid">
            {careerMembershipsConfig.membershipBenefits.cards.map((card) => (
              <article key={card.id} className="career-membership-benefits__card">
                <span className="career-membership-benefits__icon" aria-hidden="true">{getBenefitIcon(card.title)}</span>
                <h3>{card.title}</h3>
                <p>{card.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="band bg-light">
        <FAQSection
          title={careerMembershipsConfig.faq.title}
          items={careerMembershipsConfig.faq.items}
        />
      </section>

      <div className="career-sticky-cta" role="complementary" aria-label="Membership call to action">
        <p>{careerMembershipsConfig.stickyCta.text}</p>
        <a href={careerMembershipsConfig.stickyCta.href}>{careerMembershipsConfig.stickyCta.buttonLabel}</a>
      </div>
    </main>
  );
}

export default CareerPlans;